import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fallbackLng } from 'i18n-settings';
import type { RefObject } from 'react';
import { useCallback } from 'react';

import {
  createQuizService,
  deleteQuizService,
  generateQuestionsService,
  translateQuizService,
  updateQuizService,
} from '@/services/httpServices/QuizServices';
import type {
  CreateQuizServiceConfig,
  DeleteQuizServiceConfig,
  GenerateQuizFromSequenceResult,
  GenerateQuizFromSequenceVariables,
  GetQuizByIdServiceConfig,
  TranslateQuizServiceConfig,
  UpdateQuizServiceConfig,
} from '@/services/httpServices/QuizServices/types';
import { getSequenceByIdService } from '@/services/httpServices/SequenceServices';
import { useCourseQuizGenerationLockStoreApi } from '@/stores/course-quiz-generation-lock';
import { AI_REVIEW_STATUS } from '@/types/ai-review-status';
import { getCurrentLocaleClient } from '@/utils/language-helpers';

import { quizByIdKeys, quizListKeys } from '../queries';

/**
 * Custom hook to handle quiz language changes efficiently
 * This uses the queryClient directly instead of passing callbacks
 */
export function useQuizLanguageChange() {
  const queryClient = useQueryClient();
  const userLanguage = getCurrentLocaleClient();
  // Change language without translation
  const changeLanguage = useCallback(
    async (quizQueryParams: GetQuizByIdServiceConfig, newLanguage: string): Promise<void> => {
      if (quizQueryParams === undefined || quizQueryParams.params === undefined) {
        return;
      }

      // Create new params with the updated language
      const updatedParams: GetQuizByIdServiceConfig = {
        ...quizQueryParams,
        params: {
          ...quizQueryParams.params,
          params: {
            ...quizQueryParams.params.params,
            quizLanguage: newLanguage,
          },
          query: {
            language: userLanguage,
          },
        },
      };

      // Invalidate the query with the new params to trigger a refetch
      await queryClient.invalidateQueries({
        queryKey: quizByIdKeys(updatedParams),
      });
    },
    [queryClient, userLanguage],
  );

  return { changeLanguage };
}

export function useCreateQuizMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CreateQuizServiceConfig) => {
      try {
        const response = await createQuizService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate all quiz list queries
      await queryClient.invalidateQueries({
        queryKey: quizListKeys(),
      });

      // No need to invalidate a specific quiz by ID since this is a new quiz
    },
  });
}

export function useCreateQuizMutationWithoutInvalidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CreateQuizServiceConfig) => {
      try {
        const response = await createQuizService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (data) => {
      // Invalidate quiz list queries to include the new quiz
      await queryClient.invalidateQueries({
        queryKey: quizListKeys(),
      });

      // Return the data so the component can access the newly created quiz ID
      return data;
    },
  });
}

export function useGenerateQuizFromSequenceMutation(
  onFlowSuccessRef: RefObject<((data: GenerateQuizFromSequenceResult) => void) | undefined>,
) {
  const courseQuizGenerationLockStore = useCourseQuizGenerationLockStoreApi();

  return useMutation({
    mutationFn: async (vars: GenerateQuizFromSequenceVariables): Promise<GenerateQuizFromSequenceResult> => {
      try {
        const {
          httpRequestConfig,
          spaceId,
          quizTitle,
          userLanguage,
          courseLanguage,
          previousSequenceItem,
        } = vars;

        const createResponse = await createQuizService({
          httpRequestConfig,
          params: {
            params: { spaceId, quizLanguage: userLanguage },
            body: { title: quizTitle },
          },
        });

        const quizId = createResponse?.data?.id;
        if (quizId === undefined || quizId === '') {
          throw new Error('Quiz creation returned no ID.');
        }

        const sequenceResponse = await getSequenceByIdService({
          httpRequestConfig,
          params: {
            params: {
              spaceId,
              id: previousSequenceItem.id,
              sequenceLanguage: courseLanguage ?? userLanguage,
            },
            query: { language: userLanguage },
          },
        });

        const stepTexts = (sequenceResponse.data.steps ?? [])
          .map(step => step.text)
          .filter((text): text is string => text !== undefined && text.trim() !== '');

        const generatedQuestions = stepTexts.length > 0
          ? await generateQuestionsService({
              httpRequestConfig,
              params: {
                body: {
                  steps: stepTexts,
                  language: courseLanguage ?? userLanguage,
                },
              },
            })
          : [];

        await updateQuizService({
          httpRequestConfig,
          params: {
            params: { spaceId, id: quizId, quizLanguage: userLanguage },
            query: { language: userLanguage },
            body: {
              title: quizTitle,
              questions: generatedQuestions.map(q => ({
                question: q.question,
                answers: q.answers,
                aiReviewStatus: AI_REVIEW_STATUS.AI_PENDING,
                aiGeneratedFromSequence: {
                  id: previousSequenceItem.id,
                  title: previousSequenceItem.title,
                },
              })),
            },
          },
        });

        return { quizId, quizTitle };
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onMutate: (variables) => {
      courseQuizGenerationLockStore.getState().startGenerating(variables.itemIndex);
    },
    onSettled: () => {
      courseQuizGenerationLockStore.getState().stopGenerating();
    },
    onSuccess: (data) => {
      onFlowSuccessRef.current?.(data);
    },
  });
}

export function useUpdateQuizMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UpdateQuizServiceConfig) => {
      try {
        const response = await updateQuizService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate all quiz list queries
      await queryClient.invalidateQueries({
        queryKey: quizListKeys(),
      });

      // Invalidate the specific quiz by ID query
      await queryClient.invalidateQueries({
        queryKey: quizByIdKeys(variables),
      });
    },
  });
}

export function useDeleteQuizMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: DeleteQuizServiceConfig) => {
      try {
        await deleteQuizService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, _variables) => {
      // Only invalidate quiz list queries - don't invalidate the specific quiz
      // that was just deleted, as this causes unnecessary refetches
      await queryClient.invalidateQueries({
        queryKey: quizListKeys(),
      });
    },
  });
}

export function useTranslateQuizMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: TranslateQuizServiceConfig) => {
      try {
        const response = await translateQuizService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate all quiz list queries
      await queryClient.invalidateQueries({
        queryKey: quizListKeys(),
      });

      // Invalidate the specific quiz by ID query
      await queryClient.invalidateQueries({
        queryKey: quizByIdKeys(variables),
      });
    },
  });
}

export function usePublishQuizMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: Omit<UpdateQuizServiceConfig, 'params'> & {
      params: UpdateQuizServiceConfig['params'] & {
        body?: null;
      };
    }) => {
      const publishConfig: UpdateQuizServiceConfig = {
        ...config,
        params: {
          ...config.params,
          query: {
            ...config.params.query,
            additionalAction: 'publish',
          },
          body: null,
        },
      };
      return updateQuizService(publishConfig);
    },
    onSuccess: async (_, variables) => {
      // Invalidate quiz-related queries
      await queryClient.invalidateQueries({
        queryKey: quizListKeys(),
      });
      await queryClient.invalidateQueries({
        queryKey: quizByIdKeys(variables),
      });

      // Invalidate validation queries specifically for this unit
      const { unitValidationKeys } = await import('../queries/courseQueryKeys');
      const unitId = variables.params?.params?.id;
      const spaceId = variables.params?.params?.spaceId;
      if (unitId && spaceId) {
        // Create a validation config to target this specific unit
        const validationConfig = {
          httpRequestConfig: variables.httpRequestConfig,
          params: {
            params: {
              spaceId,
            },
            body: {
              unitId,
              unitType: 'quiz' as const,
              language: variables.params?.query?.language || getCurrentLocaleClient() || fallbackLng,
            },
          },
        };

        // Only invalidate validation queries for this specific unit
        await queryClient.invalidateQueries({
          queryKey: unitValidationKeys(validationConfig),
        });
      }
    },
  });
}
